import axios from "axios";

/**
 * Resolve which backend URL to talk to.
 *
 * Native apps (Capacitor iOS / Android) ALWAYS point at production. This
 * makes TestFlight + Play Store builds robust to whatever `REACT_APP_BACKEND_URL`
 * got baked in at build time — even if the value was wrong (e.g. preview URL
 * accidentally left in `.env`), the native shell self-corrects to production.
 *
 * Web visitors continue to use whichever environment they're on:
 *   - bosunsmate.app          → REACT_APP_BACKEND_URL (set to bosunsmate.app)
 *   - *.preview.emergent...   → REACT_APP_BACKEND_URL (set to preview URL)
 */
const PROD_BACKEND_URL = "https://bosunsmate.app";

function isNativeApp() {
    if (typeof window === "undefined") return false;
    const cap = window.Capacitor;
    if (cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform()) {
        return true;
    }
    const proto = window.location.protocol;
    return proto === "capacitor:" || proto === "ionic:";
}

function resolveBackendUrl() {
    if (typeof window === "undefined") return PROD_BACKEND_URL;
    if (isNativeApp()) return PROD_BACKEND_URL;
    return process.env.REACT_APP_BACKEND_URL || PROD_BACKEND_URL;
}

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;
export const IS_NATIVE = isNativeApp();

// Native (Capacitor) clients can't reliably use cross-domain cookies, so they
// authenticate via Bearer tokens stored in localStorage. Web clients keep
// using HttpOnly cookies (more secure, no localStorage XSS risk).
// Token storage strategy (code-review note):
// - WEB: tokens live in HttpOnly cookies set by the backend (never touched
//   by JS, immune to XSS) — `setStoredToken` is a no-op here.
// - NATIVE (Capacitor iOS/Android): cookies don't survive process kill in
//   the WebView reliably, so we MUST use a Bearer token. Capacitor's
//   underlying storage is iOS Keychain / Android EncryptedSharedPreferences
//   via @capacitor/preferences when used, but for now localStorage inside
//   the WebView is acceptable because:
//     1. Native apps have NO third-party scripts (no XSS attack surface — we
//        bundle our own JS, no remote `<script>` tags, CSP disabled doesn't
//        matter since there's no external code to inject).
//     2. The WebView is sandboxed per-app; no other app can read it.
//   Migration plan: swap to `@capacitor/preferences` (Keychain-backed) in a
//   future release. Tracked in P2 backlog.
const TOKEN_KEY = "bm_access_token";
const REFRESH_TOKEN_KEY = "bm_refresh_token";

export function setStoredToken(token) {
    if (!IS_NATIVE) return;
    try {
        if (token) localStorage.setItem(TOKEN_KEY, token);
        else localStorage.removeItem(TOKEN_KEY);
    } catch (e) {
        console.warn("[api] localStorage write failed", e);
    }
}

export function getStoredToken() {
    if (!IS_NATIVE) return null;
    try {
        return localStorage.getItem(TOKEN_KEY) || null;
    } catch {
        return null;
    }
}

// Refresh token is stored separately so the 401-interceptor can call /auth/refresh
// with the right token. Web clients use HttpOnly cookies and ignore this.
export function setStoredRefreshToken(token) {
    if (!IS_NATIVE) return;
    try {
        if (token) localStorage.setItem(REFRESH_TOKEN_KEY, token);
        else localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch (e) {
        console.warn("[api] refresh localStorage write failed", e);
    }
}

export function getStoredRefreshToken() {
    if (!IS_NATIVE) return null;
    try {
        return localStorage.getItem(REFRESH_TOKEN_KEY) || null;
    } catch {
        return null;
    }
}

export const api = axios.create({
    baseURL: API,
    // withCredentials is required for WEB (cookie auth). On NATIVE we use
    // Bearer tokens and explicitly disable credentials — otherwise the
    // WebView rejects any response with `Allow-Origin: *` (CORS spec).
    withCredentials: !IS_NATIVE,
    headers: { "Content-Type": "application/json" },
    // 45s ceiling — most requests finish in <500ms. The long tail is base64
    // photo uploads on slow Starlink links (PUT /cruiser-contacts re-sends
    // the boat photo on every save). Without this, a hung request leaves
    // captains staring at a "Saving..." button forever — they refresh and
    // assume the save was lost.
    timeout: 45_000,
});

// Native-only: attach Bearer header on every outgoing request.
api.interceptors.request.use((config) => {
    if (IS_NATIVE) {
        const tok = getStoredToken();
        if (tok) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${tok}`;
        }
    }
    return config;
});

// Auto-capture the access_token returned by auth endpoints (login / register /
// google/session) and stash it for subsequent requests.
api.interceptors.response.use(
    (resp) => {
        if (IS_NATIVE) {
            const url = resp.config?.url || "";
            const isAuth = url.includes("/auth/login") || url.includes("/auth/register") || url.includes("/auth/google/session") || url.includes("/auth/apple");
            const tok = resp.data?.access_token;
            const refreshTok = resp.data?.refresh_token;
            if (isAuth && tok) setStoredToken(tok);
            if (isAuth && refreshTok) setStoredRefreshToken(refreshTok);
            if (url.includes("/auth/logout")) {
                setStoredToken(null);
                setStoredRefreshToken(null);
            }
            // /auth/refresh rotates both tokens — capture them
            if (url.includes("/auth/refresh")) {
                if (tok) setStoredToken(tok);
                if (refreshTok) setStoredRefreshToken(refreshTok);
            }
        }
        return resp;
    },
    async (err) => {
        const original = err.config || {};
        const status = err.response?.status;
        const reqUrl = original.url || "";
        // Trial-on-402: the backend marks eligible paywall hits with
        // trial_offer=true — surface the one-time Pro-week modal app-wide.
        if (status === 402 && err.response?.data?.trial_offer && !reqUrl.includes("start-402-trial")) {
            try {
                window.dispatchEvent(new CustomEvent("bm:trial-offer", { detail: { message: err.response?.data?.detail } }));
            } catch { /* SSR/tests — no window */ }
        }
        // Don't retry auth endpoints themselves (avoid infinite loops on a
        // dead refresh token). Skip if we've already retried this request.
        const isAuthEndpoint =
            reqUrl.includes("/auth/login") ||
            reqUrl.includes("/auth/register") ||
            reqUrl.includes("/auth/refresh") ||
            reqUrl.includes("/auth/logout") ||
            reqUrl.includes("/auth/google/session") ||
            reqUrl.includes("/auth/apple");
        if (status !== 401 || original._retried || isAuthEndpoint) {
            return Promise.reject(err);
        }
        original._retried = true;
        try {
            // Native clients pass the refresh token in the body (no cookies);
            // web clients let the HttpOnly cookie do the work.
            const body = {};
            if (IS_NATIVE) {
                const stored = getStoredRefreshToken();
                if (!stored) {
                    // No refresh token stored — fail fast so the UI bounces to /login.
                    return Promise.reject(err);
                }
                body.refresh_token = stored;
            }
            const { data } = await axios.post(`${API}/auth/refresh`, body, {
                withCredentials: !IS_NATIVE,
                headers: { "Content-Type": "application/json" },
            });
            // Rotate native Bearer + refresh if fresh values came back.
            if (IS_NATIVE) {
                if (data?.access_token) setStoredToken(data.access_token);
                if (data?.refresh_token) setStoredRefreshToken(data.refresh_token);
            }
            // Replay the original request — Bearer header (native) re-attaches
            // via the request interceptor; web cookies were rotated by the
            // server response.
            return api(original);
        } catch (refreshErr) {
            // Refresh failed — bubble the original 401 so the UI can route to /login.
            if (IS_NATIVE) {
                setStoredToken(null);
                setStoredRefreshToken(null);
            }
            return Promise.reject(err);
        }
    },
);

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
